/**
 * DataChart Style Registry - aligned with traitStyleRegistry
 * Each chart supports multiple styles (Style01, Style02, ...)
 */
import type { ComponentType } from 'react'
import { TemperatureChartStyle01 } from './TemperatureChartStyle01'
import { TemperatureChartStyle02 } from './TemperatureChartStyle02'
import { EnergyChartStyle01 } from './EnergyChartStyle01'
import { EnergyChartStyle02 } from './EnergyChartStyle02'
import { EnergyChartStyle03 } from './EnergyChartStyle03'
import { EnergyChartStyle04 } from './EnergyChartStyle04'
import { AirQualityChartStyle01 } from './AirQualityChartStyle01'
import { AirQualityChartStyle02 } from './AirQualityChartStyle02'
import { EnergyCardStyle01 } from './EnergyCardStyle01'
import { EnergyCardStyle02 } from './EnergyCardStyle02'
import { EnergyCardStyle03 } from './EnergyCardStyle03'
import { EnergyCardStyle04 } from './EnergyCardStyle04'
import { AirQualityCardStyle01 } from './AirQualityCardStyle01'
import { TemperatureCardStyle01 } from './TemperatureCardStyle01'
import { TemperatureCardStyle02 } from './TemperatureCardStyle02'
import { PresenceActivityStyle01 } from './PresenceActivityStyle01'

export interface DataChartStyleProps {
  deviceSN?: string | null
  credentials?: { apiKey?: string; accessToken?: string; appId?: string }
}

/** Chart style descriptions */
export const DATA_CHART_STYLE_DESCRIPTIONS: Record<string, Record<string, string>> = {
  TemperatureChart: {
    Style01: 'Dark AreaChart, blue gradient, range dropdown + sensor switch',
    Style02: 'Dark AreaChart, gold gradient, range dropdown + sensor switch',
  },
  EnergyChart: {
    Style01: 'Dark AreaChart, blue gradient, range dropdown + energy sensor switch',
    Style02: 'Dark BarChart, gold, range dropdown + energy sensor switch',
    Style03: '三相電 AreaChart, blue, slider 總計→R→S→T',
    Style04: '三相電 BarChart, gold, slider 總計→R→S→T',
  },
  AirQualityChart: {
    Style01: 'Dark AreaChart, blue gradient, range dropdown + air quality sensor switch',
    Style02: 'Dark BarChart, gold, range dropdown + air quality sensor switch',
  },
  EnergyCard: {
    Style01: 'PowerCard Pure Numeric（三相數值欄位 + Combined Total）',
    Style02: 'PowerCard 3-Phase Monitoring（Stitch mobile，移除 Frequency / Power Factor）',
    Style03: 'PowerCard 3-Phase Monitoring（半圓 Gauge 卡片版）',
    Style04: 'PowerCard Single-Phase（L-N 即時監測）',
  },
  AirQualityCard: {
    Style01: 'Air Quality 指標卡（AQI, PM2.5, PM10, CO2, TVOC, HCHO, Temperature, Humidity）',
  },
  TemperatureCard: {
    Style01: 'Temperature + Humidity 雙指標卡（同時顯示）',
    Style02: 'Temp & Humidity Sensor Display（Stitch 抽取版）',
  },
  PresenceActivity: {
    Style01: '24h Activity（Presence timeline card）',
  },
}

export const DATA_CHART_STYLE_REGISTRY: Record<string, Record<string, ComponentType<DataChartStyleProps>>> = {
  TemperatureChart: {
    Style01: TemperatureChartStyle01,
    Style02: TemperatureChartStyle02,
  },
  EnergyChart: {
    Style01: EnergyChartStyle01,
    Style02: EnergyChartStyle02,
    Style03: EnergyChartStyle03,
    Style04: EnergyChartStyle04,
  },
  AirQualityChart: {
    Style01: AirQualityChartStyle01,
    Style02: AirQualityChartStyle02,
  },
  EnergyCard: {
    Style01: EnergyCardStyle01,
    Style02: EnergyCardStyle02,
    Style03: EnergyCardStyle03,
    Style04: EnergyCardStyle04,
  },
  AirQualityCard: {
    Style01: AirQualityCardStyle01,
  },
  TemperatureCard: {
    Style01: TemperatureCardStyle01,
    Style02: TemperatureCardStyle02,
  },
  PresenceActivity: {
    Style01: PresenceActivityStyle01,
  },
}

const DEVICE_TYPE_CHART_STYLE_DEFAULTS: Record<string, Record<string, string>> = {
  electrical_sensor_1p3w: {
    EnergyCard: 'Style04',
    EnergyChart: 'Style01',
  },
  electrical_sensor_3p4w: {
    EnergyCard: 'Style02',
    EnergyChart: 'Style03',
  },
  // Backward-compatible aliases while legacy types still exist in old templates.
  power_meter: {
    EnergyCard: 'Style04',
    EnergyChart: 'Style01',
  },
  power_meter_3phase: {
    EnergyCard: 'Style02',
    EnergyChart: 'Style03',
  },
}

function normalizeDeviceTypeToken(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

export function pickChartStyleForDeviceType(chart: string, deviceType: string): string | undefined {
  const chartId = String(chart || '').trim()
  const typeToken = normalizeDeviceTypeToken(deviceType)
  if (!chartId || !typeToken) return undefined
  return DEVICE_TYPE_CHART_STYLE_DEFAULTS[typeToken]?.[chartId]
}

export function getChartStyleDescription(chart: string, styleId: string): string | undefined {
  return DATA_CHART_STYLE_DESCRIPTIONS[chart]?.[styleId]
}

export function getChartStyleComponent(
  chart: string,
  styleId: string
): ComponentType<DataChartStyleProps> | null {
  const normalizedChart = chart?.trim() || ''
  const normalizedStyle = styleId?.trim() || 'Style01'
  const chartMap = DATA_CHART_STYLE_REGISTRY[normalizedChart]
  if (!chartMap) return null
  return chartMap[normalizedStyle] ?? chartMap.Style01 ?? null
}

export function getStyleIdsForChart(chart: string): string[] {
  const chartMap = DATA_CHART_STYLE_REGISTRY[chart?.trim() || '']
  return chartMap ? Object.keys(chartMap) : []
}
